// Client-side avatar prep: center-crop to a square and downscale to a small
// JPEG before upload. Keeps stored avatars tiny and uniform.

export interface SquareCrop {
  sx: number;
  sy: number;
  side: number;
}

/** Center-crop geometry for turning a WxH image into a square. */
export function computeSquareCrop(w: number, h: number): SquareCrop {
  const side = Math.min(w, h);
  return { sx: (w - side) / 2, sy: (h - side) / 2, side };
}

/**
 * Load `file`, center-crop to a square, downscale to `size`x`size`, and return
 * a JPEG Blob. Rejects non-image files and decode failures.
 */
export function resizeImageToSquare(file: File, size = 512): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Not an image'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const { sx, sy, side } = computeSquareCrop(img.naturalWidth, img.naturalHeight);
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas unsupported'));
          return;
        }
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Encode failed'))),
          'image/jpeg',
          0.9,
        );
      } catch (e) {
        reject(e as Error);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image decode failed'));
    };
    img.src = url;
  });
}

/** A crop rectangle in the source image's natural pixels (react-easy-crop's
 *  `croppedAreaPixels` shape). */
export interface PixelCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Load `file`, crop to the user-selected `area` (natural pixels), downscale to
 * `size`x`size`, and return a JPEG Blob. Unlike `resizeImageToSquare` (which
 * silently center-crops), this honours an interactive crop selection.
 */
export function cropImageToBlob(file: File, area: PixelCrop, size = 512): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Not an image'));
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas unsupported'));
          return;
        }
        // Clamp the crop rect to the image bounds so rounding never reads
        // outside the source (which would taint the canvas / draw nothing).
        const sx = Math.max(0, Math.min(area.x, img.naturalWidth));
        const sy = Math.max(0, Math.min(area.y, img.naturalHeight));
        const sw = Math.max(1, Math.min(area.width, img.naturalWidth - sx));
        const sh = Math.max(1, Math.min(area.height, img.naturalHeight - sy));
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Encode failed'))),
          'image/jpeg',
          0.9,
        );
      } catch (e) {
        reject(e as Error);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image decode failed'));
    };
    img.src = url;
  });
}
